/**
 * Analytics service for Vocalira
 * Tracks user sessions and events
 */
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

let sessionId = null;
let sessionStartTime = null;

/**
 * Start a new analytics session
 */
export const startSession = async (initialPage = 'dashboard') => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return;

    const deviceInfo = {
      userAgent: navigator.userAgent,
      language: navigator.language,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      platform: navigator.platform
    };

    const response = await axios.post(
      `${API}/api/analytics/session/start`,
      { initial_page: initialPage, device_info: deviceInfo },
      { headers: { Authorization: `Bearer ${token}` } }
    );

    if (response.data.success) {
      sessionId = response.data.session_id;
      sessionStartTime = Date.now();
    }
  } catch (error) {
    console.error('Analytics session start error:', error);
  }
};

/**
 * End the current analytics session
 */
export const endSession = async () => {
  try {
    if (!sessionId) return;

    const token = localStorage.getItem('token');
    if (!token) return;

    await axios.post(
      `${API}/api/analytics/session/end`,
      { session_id: sessionId },
      { headers: { Authorization: `Bearer ${token}` } }
    );

    sessionId = null;
    sessionStartTime = null;
  } catch (error) {
    console.error('Analytics session end error:', error);
  }
};

/**
 * Track a custom event
 */
export const trackEvent = async (eventType, eventData = {}) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return;

    await axios.post(
      `${API}/api/analytics/event`,
      { event_type: eventType, event_data: eventData },
      { headers: { Authorization: `Bearer ${token}` } }
    );
  } catch (error) {
    console.error('Analytics track error:', error);
  }
};

// Predefined event types
export const EVENTS = {
  // Onboarding
  ONBOARDING_START: 'onboarding_start',
  ONBOARDING_COMPLETE: 'onboarding_complete',
  ONBOARDING_ABANDON: 'onboarding_abandon',
  
  // Chat
  CHAT_START: 'chat_start',
  MESSAGE_SENT: 'message_sent',
  VOICE_USED: 'voice_used',
  
  // Learning
  LESSON_START: 'lesson_start',
  LESSON_COMPLETE: 'lesson_complete',
  QUIZ_COMPLETE: 'quiz_complete',
  GUIDED_LESSON_START: 'guided_lesson_start',
  GUIDED_LESSON_COMPLETE: 'guided_lesson_complete',
  
  // Vocabulary
  WORD_SAVED: 'word_saved',
  FLASHCARD_REVIEW: 'flashcard_review',
  
  // Gamification
  MISSION_COMPLETE: 'mission_complete',
  BADGE_EARNED: 'badge_earned',
  STREAK_ACHIEVED: 'streak_achieved',
  
  // Premium
  UPGRADE_MODAL_SHOWN: 'upgrade_modal_shown',
  UPGRADE_CLICKED: 'upgrade_clicked',
  UPGRADE_COMPLETE: 'upgrade_complete',
  TTS_PREMIUM_USED: 'tts_premium_used',
  
  // Listening & Practice
  LISTENING_PRACTICE_START: 'listening_practice_start',
  LISTENING_EXERCISE_COMPLETE: 'listening_exercise_complete',
  
  // General
  PAGE_VIEW: 'page_view',
  BUTTON_CLICK: 'button_click',
  FEATURE_USED: 'feature_used',
  ERROR: 'error'
};

// Auto-end session on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    if (sessionId) {
      // Use sendBeacon for reliable delivery on page unload
      const token = localStorage.getItem('token');
      if (token) {
        navigator.sendBeacon(
          `${API}/api/analytics/session/end`,
          JSON.stringify({ session_id: sessionId })
        );
      }
    }
  });
}
